// ---------------------------------------------------------------------------
// Global Public Webcam Directory — curated live camera feeds
// Sources: YouTube 24/7 live streams, EarthCam, tourism authority cams
// Strategy: hlsUrl → videoId (YouTube embed) → websiteUrl (fallback)
//
// ⚠ videoId 를 추가할 때는 반드시 생존 확인을 거칠 것.
//   2026-09-23 감사에서 등록된 38개 videoId 중 37개가 삭제(REMOVED),
//   1개는 방송 종료 상태였고, 7쌍은 서로 다른 도시가 같은 영상을 가리키고 있었다
//   (예: Seoul Gangnam 과 Hong Kong Victoria Harbour 가 동일 ID).
//   판정 기준: 페이지의 "isLiveContent":true 여부. "UNPLAYABLE" 문자열은
//   살아있는 스트림 페이지에도 등장하므로 판정에 쓰면 안 된다.
//   지역 실시간 커버리지는 Windy API(좌표 nearby 조회)가 주력이다.
// ---------------------------------------------------------------------------

export type WebcamRegion = 'asia' | 'europe' | 'americas' | 'mideast' | 'africa' | 'oceania';
export type WebcamType = 'landmark' | 'cityview' | 'nature' | 'port' | 'traffic';

export interface PublicCam {
  id: string;
  name: string;
  city: string;
  country: string;
  flag: string;
  lat: number;
  lon: number;
  hlsUrl?: string;
  videoId?: string;      // YouTube live video ID
  websiteUrl?: string;   // Reliable fallback link
  region: WebcamRegion;
  type: WebcamType;
  description?: string;
}

export const PUBLIC_CAMS: PublicCam[] = [
  // ── Asia ──────────────────────────────────────────────────────────────────
  {
    id: 'shibuya-crossing',
    name: 'Shibuya Crossing',
    city: 'Tokyo', country: 'Japan', flag: '🇯🇵',
    lat: 35.6595, lon: 139.7004,
    websiteUrl: 'https://www.earthtv.com/en/webcam/tokyo-shibuya-crossing',
    region: 'asia', type: 'landmark',
    description: 'World\'s busiest pedestrian crossing',
  },
  {
    id: 'tokyo-skytree',
    name: 'Tokyo Skytree View',
    city: 'Tokyo', country: 'Japan', flag: '🇯🇵',
    lat: 35.7101, lon: 139.8107,
    websiteUrl: 'https://www.tokyo-skytree.jp/en/',
    region: 'asia', type: 'landmark',
    description: '634m tallest tower in Japan',
  },
  {
    id: 'seoul-hangang',
    // 실측 확인(2026-09-23): isLiveContent=true, 임베드 허용, 24/7 방송 중
    name: 'Hangang · Banpo Bridge',
    city: 'Seoul', country: 'South Korea', flag: '🇰🇷',
    lat: 37.5126, lon: 126.9966,
    videoId: 'RVvYQghFCXk',
    websiteUrl: 'https://www.youtube.com/watch?v=RVvYQghFCXk',
    region: 'asia', type: 'cityview',
    description: '4K live — Han River & Banpo Bridge, 24/7',
  },
  {
    id: 'seoul-gangnam',
    name: 'Gangnam District',
    city: 'Seoul', country: 'South Korea', flag: '🇰🇷',
    lat: 37.4979, lon: 127.0276,
    websiteUrl: 'https://english.seoul.go.kr/',
    region: 'asia', type: 'cityview',
    description: 'Seoul\'s financial & tech hub',
  },
  {
    id: 'hong-kong-victoria',
    name: 'Victoria Harbour',
    city: 'Hong Kong', country: 'China (HK)', flag: '🇭🇰',
    lat: 22.2885, lon: 114.1577,
    websiteUrl: 'https://www.discoverhongkong.com/',
    region: 'asia', type: 'landmark',
    description: 'Victoria Harbour panorama',
  },
  {
    id: 'singapore-mbs',
    name: 'Marina Bay Sands',
    city: 'Singapore', country: 'Singapore', flag: '🇸🇬',
    lat: 1.2831, lon: 103.8607,
    websiteUrl: 'https://www.marinabaysands.com/',
    region: 'asia', type: 'landmark',
    description: 'Marina Bay skyline & rooftop',
  },
  {
    id: 'bangkok-cityview',
    name: 'Bangkok Skyline',
    city: 'Bangkok', country: 'Thailand', flag: '🇹🇭',
    lat: 13.7563, lon: 100.5018,
    websiteUrl: 'https://www.tourismthailand.org/',
    region: 'asia', type: 'cityview',
    description: 'Bangkok city skyline live cam',
  },
  {
    id: 'taipei-101',
    name: 'Taipei 101',
    city: 'Taipei', country: 'Taiwan', flag: '🇹🇼',
    lat: 25.0330, lon: 121.5654,
    websiteUrl: 'https://www.taipei-101.com.tw/en',
    region: 'asia', type: 'landmark',
    description: 'Taipei skyline from 101 tower',
  },
  {
    id: 'beijing-tiananmen',
    name: 'Tiananmen Square',
    city: 'Beijing', country: 'China', flag: '🇨🇳',
    lat: 39.9054, lon: 116.3976,
    websiteUrl: 'https://english.visitbeijing.com.cn/',
    region: 'asia', type: 'landmark',
    description: 'World\'s largest public square',
  },
  {
    id: 'shanghai-bund',
    name: 'The Bund',
    city: 'Shanghai', country: 'China', flag: '🇨🇳',
    lat: 31.2397, lon: 121.4903,
    websiteUrl: 'https://www.meet-in-shanghai.net/',
    region: 'asia', type: 'landmark',
    description: 'Historic waterfront & Pudong skyline',
  },
  {
    id: 'kuala-lumpur-twin',
    name: 'Petronas Twin Towers',
    city: 'Kuala Lumpur', country: 'Malaysia', flag: '🇲🇾',
    lat: 3.1579, lon: 101.7116,
    websiteUrl: 'https://www.petronastwintowers.com.my/',
    region: 'asia', type: 'landmark',
    description: 'Iconic KL twin towers at 452m',
  },
  {
    id: 'vietnam-halong',
    name: 'Ha Long Bay',
    city: 'Ha Long', country: 'Vietnam', flag: '🇻🇳',
    lat: 20.9101, lon: 107.1839,
    websiteUrl: 'https://halong.gov.vn/',
    region: 'asia', type: 'nature',
    description: 'UNESCO World Heritage bay with limestone karsts',
  },

  // ── Europe ────────────────────────────────────────────────────────────────
  {
    id: 'paris-eiffel',
    name: 'Eiffel Tower',
    city: 'Paris', country: 'France', flag: '🇫🇷',
    lat: 48.8584, lon: 2.2945,
    websiteUrl: 'https://www.toureiffel.paris/en',
    region: 'europe', type: 'landmark',
    description: '324m iron lattice tower on Champ de Mars',
  },
  {
    id: 'london-parliament',
    name: 'Westminster Bridge',
    city: 'London', country: 'UK', flag: '🇬🇧',
    lat: 51.5007, lon: -0.1246,
    websiteUrl: 'https://www.visitlondon.com/',
    region: 'europe', type: 'landmark',
    description: 'Big Ben & Houses of Parliament',
  },
  {
    id: 'rome-colosseum',
    name: 'Roman Colosseum',
    city: 'Rome', country: 'Italy', flag: '🇮🇹',
    lat: 41.8902, lon: 12.4922,
    websiteUrl: 'https://colosseo.it/en/',
    region: 'europe', type: 'landmark',
    description: 'Ancient amphitheatre, 70 AD',
  },
  {
    id: 'barcelona-sagrada',
    name: 'Sagrada Família',
    city: 'Barcelona', country: 'Spain', flag: '🇪🇸',
    lat: 41.4036, lon: 2.1744,
    websiteUrl: 'https://sagradafamilia.org/en/',
    region: 'europe', type: 'landmark',
    description: 'Gaudí\'s unfinished basilica since 1882',
  },
  {
    id: 'amsterdam-canal',
    name: 'Amsterdam Canals',
    city: 'Amsterdam', country: 'Netherlands', flag: '🇳🇱',
    lat: 52.3676, lon: 4.9041,
    websiteUrl: 'https://www.iamsterdam.com/',
    region: 'europe', type: 'landmark',
    description: 'UNESCO World Heritage canal ring',
  },
  {
    id: 'berlin-gate',
    name: 'Brandenburg Gate',
    city: 'Berlin', country: 'Germany', flag: '🇩🇪',
    lat: 52.5163, lon: 13.3777,
    websiteUrl: 'https://www.visitberlin.de/',
    region: 'europe', type: 'landmark',
    description: 'Iconic 18th century neoclassical gate',
  },
  {
    id: 'prague-oldtown',
    name: 'Old Town Square',
    city: 'Prague', country: 'Czechia', flag: '🇨🇿',
    lat: 50.0875, lon: 14.4213,
    websiteUrl: 'https://www.prague.eu/en',
    region: 'europe', type: 'landmark',
    description: 'Prague astronomical clock & Gothic towers',
  },
  {
    id: 'athens-acropolis',
    name: 'Acropolis of Athens',
    city: 'Athens', country: 'Greece', flag: '🇬🇷',
    lat: 37.9715, lon: 23.7267,
    websiteUrl: 'https://www.theacropolismuseum.gr/en',
    region: 'europe', type: 'landmark',
    description: 'The Parthenon, 447 BC',
  },
  {
    id: 'istanbul-bosphorus',
    name: 'Bosphorus Strait',
    city: 'Istanbul', country: 'Turkey', flag: '🇹🇷',
    lat: 41.0082, lon: 28.9784,
    websiteUrl: 'https://www.goturkey.com/',
    region: 'europe', type: 'port',
    description: 'Strategic waterway between Europe & Asia',
  },
  {
    id: 'vienna-stephansdom',
    name: 'Stephansplatz',
    city: 'Vienna', country: 'Austria', flag: '🇦🇹',
    lat: 48.2085, lon: 16.3724,
    websiteUrl: 'https://www.wien.info/en',
    region: 'europe', type: 'landmark',
    description: 'St. Stephen\'s Cathedral & city center',
  },
  {
    id: 'moscow-redSquare',
    name: 'Red Square',
    city: 'Moscow', country: 'Russia', flag: '🇷🇺',
    lat: 55.7539, lon: 37.6208,
    websiteUrl: 'https://www.moscowhotels.ru/',
    region: 'europe', type: 'landmark',
    description: 'Kremlin, Lenin\'s Mausoleum, St. Basil\'s Cathedral',
  },
  {
    id: 'warsaw-oldtown',
    name: 'Warsaw Old Town',
    city: 'Warsaw', country: 'Poland', flag: '🇵🇱',
    lat: 52.2497, lon: 21.0122,
    websiteUrl: 'https://warsawtour.pl/en/',
    region: 'europe', type: 'landmark',
    description: 'UNESCO-listed rebuilt historic center',
  },

  // ── Americas ──────────────────────────────────────────────────────────────
  {
    id: 'times-square',
    name: 'Times Square',
    city: 'New York', country: 'USA', flag: '🇺🇸',
    lat: 40.7580, lon: -73.9855,
    websiteUrl: 'https://www.earthcam.com/usa/newyork/timessquare/',
    region: 'americas', type: 'landmark',
    description: 'The Crossroads of the World, NYC',
  },
  {
    id: 'las-vegas-strip',
    name: 'Las Vegas Strip',
    city: 'Las Vegas', country: 'USA', flag: '🇺🇸',
    lat: 36.1147, lon: -115.1728,
    websiteUrl: 'https://www.lasvegaswebcam.com/',
    region: 'americas', type: 'landmark',
    description: 'Casino strip & neon lights',
  },
  {
    id: 'chicago-millennium',
    name: 'Millennium Park',
    city: 'Chicago', country: 'USA', flag: '🇺🇸',
    lat: 41.8827, lon: -87.6233,
    websiteUrl: 'https://www.earthcam.com/usa/illinois/chicago/',
    region: 'americas', type: 'cityview',
    description: 'Cloud Gate ("The Bean") & Chicago skyline',
  },
  {
    id: 'sf-golden-gate',
    name: 'Golden Gate Bridge',
    city: 'San Francisco', country: 'USA', flag: '🇺🇸',
    lat: 37.8199, lon: -122.4783,
    websiteUrl: 'https://www.goldengatebridge.org/',
    region: 'americas', type: 'landmark',
    description: '1.7km suspension bridge over SF Bay',
  },
  {
    id: 'miami-beach',
    name: 'Miami Beach',
    city: 'Miami', country: 'USA', flag: '🇺🇸',
    lat: 25.7907, lon: -80.1300,
    websiteUrl: 'https://www.miamibeachwebcam.com/',
    region: 'americas', type: 'nature',
    description: 'South Beach live oceanfront',
  },
  {
    id: 'niagara-falls',
    name: 'Niagara Falls',
    city: 'Niagara', country: 'Canada/USA', flag: '🇨🇦',
    lat: 43.0799, lon: -79.0747,
    websiteUrl: 'https://www.niagarafallstourism.com/',
    region: 'americas', type: 'nature',
    description: '3,160 tons of water per second',
  },
  {
    id: 'rio-copacabana',
    name: 'Copacabana Beach',
    city: 'Rio de Janeiro', country: 'Brazil', flag: '🇧🇷',
    lat: -22.9713, lon: -43.1823,
    websiteUrl: 'https://www.rioguiaoficial.com.br/',
    region: 'americas', type: 'nature',
    description: 'Iconic 4km beach with Christ the Redeemer',
  },
  {
    id: 'mexico-city-zocalo',
    name: 'Zócalo',
    city: 'Mexico City', country: 'Mexico', flag: '🇲🇽',
    lat: 19.4326, lon: -99.1332,
    websiteUrl: 'https://www.visitmexico.com/en/',
    region: 'americas', type: 'landmark',
    description: 'One of the world\'s largest city squares',
  },
  {
    id: 'buenos-aires-obelisk',
    name: 'Obelisco de Buenos Aires',
    city: 'Buenos Aires', country: 'Argentina', flag: '🇦🇷',
    lat: -34.6037, lon: -58.3816,
    websiteUrl: 'https://www.bue.gob.ar/',
    region: 'americas', type: 'landmark',
    description: 'Buenos Aires central landmark, 9 de Julio Ave',
  },
  {
    id: 'toronto-cn-tower',
    name: 'CN Tower View',
    city: 'Toronto', country: 'Canada', flag: '🇨🇦',
    lat: 43.6426, lon: -79.3871,
    websiteUrl: 'https://www.cntower.ca/',
    region: 'americas', type: 'landmark',
    description: 'CN Tower & Toronto skyline',
  },

  // ── Middle East ───────────────────────────────────────────────────────────
  {
    id: 'dubai-burj-khalifa',
    name: 'Burj Khalifa',
    city: 'Dubai', country: 'UAE', flag: '🇦🇪',
    lat: 25.1972, lon: 55.2744,
    websiteUrl: 'https://www.burjkhalifa.ae/',
    region: 'mideast', type: 'landmark',
    description: 'World\'s tallest building at 828m',
  },
  {
    id: 'dubai-marina',
    name: 'Dubai Marina',
    city: 'Dubai', country: 'UAE', flag: '🇦🇪',
    lat: 25.0819, lon: 55.1414,
    websiteUrl: 'https://www.visitdubai.com/',
    region: 'mideast', type: 'cityview',
    description: 'Dubai Marina skyline & canal',
  },
  {
    id: 'jerusalem-old-city',
    name: 'Old City Jerusalem',
    city: 'Jerusalem', country: 'Israel', flag: '🇮🇱',
    lat: 31.7767, lon: 35.2345,
    websiteUrl: 'https://www.goisrael.com/',
    region: 'mideast', type: 'landmark',
    description: 'Western Wall & Temple Mount',
  },
  {
    id: 'riyadh-kingdom-tower',
    name: 'Kingdom Tower',
    city: 'Riyadh', country: 'Saudi Arabia', flag: '🇸🇦',
    lat: 24.6886, lon: 46.6833,
    websiteUrl: 'https://www.visitsaudi.com/en',
    region: 'mideast', type: 'landmark',
    description: 'Riyadh skyline & Kingdom Tower',
  },
  {
    id: 'cairo-pyramids',
    name: 'Pyramids of Giza',
    city: 'Cairo', country: 'Egypt', flag: '🇪🇬',
    lat: 29.9792, lon: 31.1342,
    websiteUrl: 'https://www.egypt.travel/attractions/pyramids-of-giza',
    region: 'mideast', type: 'landmark',
    description: 'Great Pyramid of Giza, 2560 BC',
  },
  {
    id: 'tehran-milad',
    name: 'Milad Tower',
    city: 'Tehran', country: 'Iran', flag: '🇮🇷',
    lat: 35.7444, lon: 51.3747,
    websiteUrl: 'https://miladtower.ir/',
    region: 'mideast', type: 'landmark',
    description: 'Tehran skyline from Milad Tower (435m)',
  },

  // ── Africa ────────────────────────────────────────────────────────────────
  {
    id: 'cape-town-table',
    name: 'Table Mountain',
    city: 'Cape Town', country: 'South Africa', flag: '🇿🇦',
    lat: -33.9628, lon: 18.4098,
    websiteUrl: 'https://www.tablemountain.net/',
    region: 'africa', type: 'nature',
    description: 'Iconic flat-topped mountain overlooking Cape Town',
  },
  {
    id: 'nairobi-cityview',
    name: 'Nairobi City Center',
    city: 'Nairobi', country: 'Kenya', flag: '🇰🇪',
    lat: -1.2921, lon: 36.8219,
    websiteUrl: 'https://www.magicalkenya.com/',
    region: 'africa', type: 'cityview',
    description: 'East Africa\'s main financial hub',
  },
  {
    id: 'lagos-island',
    name: 'Lagos Island',
    city: 'Lagos', country: 'Nigeria', flag: '🇳🇬',
    lat: 6.4550, lon: 3.3841,
    websiteUrl: 'https://www.lagosstate.gov.ng/',
    region: 'africa', type: 'cityview',
    description: 'Africa\'s largest city, 15M+ population',
  },
  {
    id: 'casablanca-hassan',
    name: 'Hassan II Mosque',
    city: 'Casablanca', country: 'Morocco', flag: '🇲🇦',
    lat: 33.6083, lon: -7.6324,
    websiteUrl: 'https://www.visitmorocco.com/',
    region: 'africa', type: 'landmark',
    description: '3rd largest mosque in the world, on the Atlantic coast',
  },

  // ── Oceania ───────────────────────────────────────────────────────────────
  {
    id: 'sydney-opera',
    name: 'Sydney Opera House',
    city: 'Sydney', country: 'Australia', flag: '🇦🇺',
    lat: -33.8568, lon: 151.2153,
    websiteUrl: 'https://www.sydneyoperahouse.com/',
    region: 'oceania', type: 'landmark',
    description: 'UNESCO World Heritage, Bennelong Point',
  },
  {
    id: 'sydney-harbour',
    name: 'Sydney Harbour Bridge',
    city: 'Sydney', country: 'Australia', flag: '🇦🇺',
    lat: -33.8523, lon: 151.2108,
    websiteUrl: 'https://www.bridgeclimb.com/',
    region: 'oceania', type: 'landmark',
    description: 'World\'s largest steel arch bridge',
  },
  {
    id: 'auckland-sky-tower',
    name: 'Sky Tower Auckland',
    city: 'Auckland', country: 'New Zealand', flag: '🇳🇿',
    lat: -36.8682, lon: 174.7616,
    websiteUrl: 'https://www.skycityauckland.co.nz/',
    region: 'oceania', type: 'landmark',
    description: 'Southern Hemisphere\'s tallest freestanding structure',
  },
  {
    id: 'great-barrier-reef',
    name: 'Great Barrier Reef',
    city: 'Cairns', country: 'Australia', flag: '🇦🇺',
    lat: -18.2861, lon: 147.7000,
    websiteUrl: 'https://www.gbrmpa.gov.au/',
    region: 'oceania', type: 'nature',
    description: 'World\'s largest coral reef system, 2,300km',
  },

  // ── Strategic Maritime ─────────────────────────────────────────────────────
  {
    id: 'strait-of-hormuz',
    name: 'Strait of Hormuz',
    city: 'Muscat', country: 'Oman/Iran', flag: '🇴🇲',
    lat: 26.5667, lon: 56.4333,
    websiteUrl: 'https://marinetraffic.com/',
    region: 'mideast', type: 'port',
    description: '21% of global oil transit choke point',
  },
  {
    id: 'suez-canal',
    name: 'Suez Canal',
    city: 'Ismailia', country: 'Egypt', flag: '🇪🇬',
    lat: 30.5852, lon: 32.2654,
    websiteUrl: 'https://www.suezcanal.gov.eg/',
    region: 'mideast', type: 'port',
    description: '12% of global trade passes through here daily',
  },
  {
    id: 'singapore-port',
    name: 'Port of Singapore',
    city: 'Singapore', country: 'Singapore', flag: '🇸🇬',
    lat: 1.2649, lon: 103.8198,
    websiteUrl: 'https://www.mpa.gov.sg/',
    region: 'asia', type: 'port',
    description: 'World\'s 2nd busiest container port',
  },
  {
    id: 'rotterdam-port',
    name: 'Port of Rotterdam',
    city: 'Rotterdam', country: 'Netherlands', flag: '🇳🇱',
    lat: 51.9225, lon: 4.4792,
    websiteUrl: 'https://www.portofrotterdam.com/',
    region: 'europe', type: 'port',
    description: 'Europe\'s largest port, 450M tons/year',
  },
];

export const CAM_REGIONS: { id: WebcamRegion | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'asia', label: 'Asia' },
  { id: 'europe', label: 'Europe' },
  { id: 'americas', label: 'Americas' },
  { id: 'mideast', label: 'M.East' },
  { id: 'africa', label: 'Africa' },
  { id: 'oceania', label: 'Oceania' },
];
